
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Bell, MapPin, MessageSquare, Clock, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const VolunteerSection: React.FC = () => {
  const benefits = [
    {
      icon: <Bell className="h-5 w-5 text-care-500" />,
      title: "Emergency Alerts",
      description: "Receive notifications when help is needed in your area."
    },
    {
      icon: <MessageSquare className="h-5 w-5 text-care-500" />,
      title: "Simple Communication",
      description: "Easy-to-use messaging system to coordinate check-ins."
    },
    {
      icon: <Clock className="h-5 w-5 text-care-500" />,
      title: "Flexible Scheduling",
      description: "Choose when you're available to help on your own terms."
    },
    {
      icon: <Shield className="h-5 w-5 text-care-500" />,
      title: "Safety Protocols",
      description: "Clear guidelines to ensure everyone's safety and security."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-care-50 to-white overflow-hidden relative">
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-care-100 rounded-full opacity-50 blur-3xl" />
      
      <div className="container-custom relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.45, 0, 0.2, 1] }}
          >
            <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-care-100 text-care-800 mb-4">
              Community Support
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-balance">
              Join our network of <span className="title-gradient">local volunteers</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 text-balance">
              Help create a safety net for elderly individuals in your community. As a volunteer, you can provide crucial check-ins during emergencies and be part of a caring neighborhood support system.
            </p>
            
            <div className="space-y-6 mb-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <div className="mt-1 bg-white rounded-full p-2 shadow-sm">
                    {benefit.icon}
                  </div>
                  <div className="ml-4">
                    <h3 className="font-medium text-care-800">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button className="btn-primary">
                Become a Volunteer
              </Button>
              <Link to="/emergency-request">
                <Button 
                  variant="outline" 
                  className="border-care-200 text-care-700 w-full"
                >
                  Request Emergency Check-in
                </Button>
              </Link>
            </div>
          </motion.div>
          
          <motion.div
            className="relative lg:pl-10"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.45, 0, 0.2, 1] }}
          >
            <div className="glass-morphism rounded-2xl overflow-hidden shadow-xl">
              <div className="bg-care-600 text-white py-4 px-6">
                <h3 className="font-semibold text-lg">Community Alert</h3>
              </div>
              <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 text-sm">Emergency check-in requested nearby. Can you help?</p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <p className="text-sm">Alice Thompson needs assistance</p>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-care-500 mr-2" />
                    <p className="text-sm">1.2 miles away</p>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-care-500 mr-2" />
                    <p className="text-sm">Requested 5 minutes ago</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <Button className="w-full bg-care-500 hover:bg-care-600 text-white">
                    Accept
                  </Button>
                  <Button variant="outline" className="w-full border-care-200 text-care-700 hover:bg-care-50">
                    Decline
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-care-100 rounded-full opacity-50 -z-10"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VolunteerSection;
